// AnalysisDetail.tsx - Updated to work with Firebase Realtime Database

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getAnalysisById, StoredAnalysisResult } from "@/lib/firebaseStorageService";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, FileText, Eye } from "lucide-react";
import AnalysisResults from "@/components/analyzer/AnalysisResults";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { useToast } from "@/hooks/use-toast";
import SimpleDocumentViewer from "@/components/common/SimpleDocumentViewer";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { CheckResult } from "@/lib/checkDefinitions";

// Helper function to format text for HTML
const formatTextForHTML = (text: string): string => {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\n/g, '<br>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code>$1</code>');
};

// Helper function to safely format timestamps
const formatTimestamp = (timestamp: any, formatString: string = "MMM d, yyyy 'at' h:mm a"): string => {
  try {
    if (!timestamp) {
      console.warn("formatTimestamp: timestamp is null or undefined");
      return "Unknown date";
    }
    
    let date: Date;
    
    // Handle Firestore Timestamp instances (must check first)
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    }
    // Handle Date instances
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle Firestore timestamp objects (from serialized data)
    // IMPORTANT: Check for seconds property BEFORE checking _methodName
    else if (typeof timestamp === 'object' && timestamp !== null) {
      // Check for Firestore timestamp format FIRST (most common case)
      if (timestamp.seconds !== undefined && typeof timestamp.seconds === 'number') {
        // Firestore timestamp object format: {seconds: number, nanoseconds: number}
        date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
      } else if (timestamp._seconds !== undefined && typeof timestamp._seconds === 'number') {
        // Alternative Firestore format
        date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Object with toDate method (Firestore Timestamp-like)
        try {
          date = timestamp.toDate();
        } catch (e) {
          console.error("formatTimestamp: Error calling toDate()", e);
          // Fallback to checking for seconds
          if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
          } else {
            throw new Error("Cannot convert timestamp to date");
          }
        }
      } 
      // Only check for unresolved serverTimestamp if it's truly unresolved (no seconds property)
      // This should rarely happen with properly stored data
      else if (timestamp._methodName === 'serverTimestamp' && !timestamp.seconds && !timestamp._seconds) {
        console.warn("formatTimestamp: Detected unresolved serverTimestamp - this should not happen with stored data");
        // Return "Unknown date" instead of using current date to avoid showing wrong dates
        return "Unknown date";
      } else {
        // Try to convert as-is - might be a Date-like object or other format
        date = new Date(timestamp);
      }
    }
    // Handle string/number timestamps
    else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Fallback
    else {
      console.warn("formatTimestamp: Unknown timestamp type:", typeof timestamp, timestamp);
      date = new Date(timestamp);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("formatTimestamp: Invalid date result:", date, "from timestamp:", timestamp, "type:", typeof timestamp);
      // Try to extract more information for debugging
      if (timestamp && typeof timestamp === 'object') {
        console.error("formatTimestamp: Timestamp object keys:", Object.keys(timestamp));
        console.error("formatTimestamp: Timestamp object:", JSON.stringify(timestamp, null, 2));
      }
      return "Invalid date";
    }
    
    return format(date, formatString);
  } catch (error) {
    console.error("formatTimestamp: Error formatting date:", error, "timestamp:", timestamp, "type:", typeof timestamp);
    // Try one more fallback - check if it's a number that can be converted
    if (typeof timestamp === 'number' || (typeof timestamp === 'string' && !isNaN(Number(timestamp)))) {
      try {
        const fallbackDate = new Date(Number(timestamp));
        if (!isNaN(fallbackDate.getTime())) {
          return format(fallbackDate, formatString);
        }
      } catch (e) {
        // Ignore fallback error
      }
    }
    return "Invalid date";
  }
};

// Generate HTML report
const generateHTMLReport = (analysisData: StoredAnalysisResult): string => {
  const formattedDate = formatTimestamp(analysisData.createdAt || analysisData.timestamp, "PPP 'at' p");
  
  // Generate AI Analysis Results HTML with expand/collapse
  const aiResultsHTML = Object.entries(analysisData.results)
    .filter(([checkId]) => !checkId.startsWith('user_question_'))
    .map(([checkId, result]: [string, any]) => {
      const checkTitle = checkId.replace(/_/g, ' ');
      // Handle both old and new data formats
      const isCompliant = result.isCompliant !== undefined ? result.isCompliant : result.compliant;
      const compliantClass = isCompliant ? 'compliant' : 'non-compliant';
      const compliantText = isCompliant ? 'Compliant' : 'Non-Compliant';
      
      // Get the explanation text from the appropriate field
      const explanationText = result.explanation || result.details || 'Analysis completed';
      
      return `
        <div class="result-card">
          <div class="result-header" onclick="toggleCard('${checkId}')">
            <h4 class="result-title">${checkTitle}</h4>
            <div class="header-actions">
              <span class="badge ${compliantClass}">${compliantText}</span>
              <button class="expand-btn" onclick="event.stopPropagation(); toggleCard('${checkId}')">
                <svg class="expand-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div class="result-content" id="content-${checkId}" style="display: none;">
            <div class="analysis-details">
              <h5>Analysis Details</h5>
              <div class="details-text">${formatTextForHTML(explanationText)}</div>
            </div>
            
            <div class="reasoning">
              <h5>💡 Reasoning</h5>
              <p>${formatTextForHTML(explanationText)}</p>
            </div>
            
            ${result.feedback?.evidence && result.feedback.evidence.length > 0 ? `
            <div class="evidence">
              <h5>🔍 Evidence Found</h5>
              <ul>
                ${result.feedback.evidence.map((evidence: string) => `<li>"${evidence}"</li>`).join('')}
              </ul>
            </div>
            ` : ''}
            
            ${result.feedback?.recommendations && result.feedback.recommendations.length > 0 ? `
            <div class="recommendations">
              <h5>💡 Recommendations</h5>
              <ul>
                ${result.feedback.recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
              </ul>
            </div>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');

  // Generate User Questions Results HTML with expand/collapse
  const userQuestionsHTML = Object.entries(analysisData.results)
    .filter(([checkId]) => checkId.startsWith('user_question_'))
    .map(([checkId, result]: [string, any]) => {
      const questionId = checkId.replace('user_question_', '');
      
      // Extract the actual user answer from the result - handle both old and new formats
      let userAnswer = (result as any).userAnswer;
      if (!userAnswer) {
        // Try to extract from explanation text
        const explanationText = result.explanation || result.details || '';
        const match = explanationText.match(/User Answer:\s*(YES|NO|N\/A|NA)/i);
        if (match) {
          const answer = match[1].toLowerCase();
          userAnswer = answer === 'n/a' || answer === 'na' ? 'na' : answer;
        } else {
          userAnswer = 'na'; // Default fallback
        }
      }
      
      const answerClass = userAnswer === 'yes' ? 'compliant' : userAnswer === 'no' ? 'non-compliant' : 'warning';
      const answerText = userAnswer === 'yes' ? 'Yes' : userAnswer === 'no' ? 'No' : 'N/A';
      
      // Get the explanation text
      const explanationText = result.explanation || result.details || 'User declared answer';
      
      return `
        <div class="result-card">
          <div class="result-header" onclick="toggleCard('${checkId}')">
            <h4 class="result-title">${questionId.replace(/_/g, ' ')}</h4>
            <div class="header-actions">
              <span class="badge ${answerClass}">${answerText}</span>
              <button class="expand-btn" onclick="event.stopPropagation(); toggleCard('${checkId}')">
                <svg class="expand-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          <div class="result-content" id="content-${checkId}" style="display: none;">
            <div class="user-response">
              <h5>User Answer</h5>
              <div class="details-text"><strong>${answerText}</strong>${explanationText.includes(' - ') ? ' - ' + explanationText.split(' - ').slice(1).join(' - ') : ''}</div>
            </div>
            
            <div class="reasoning">
              <h5>💡 Reasoning</h5>
              <p>${result.feedback?.reasoning || formatTextForHTML(explanationText)}</p>
            </div>
            
            ${result.feedback?.evidence && result.feedback.evidence.length > 0 ? `
            <div class="evidence">
              <h5>🔍 Additional Notes</h5>
              <ul>
                ${result.feedback.evidence.map((evidence: string) => `<li>"${evidence}"</li>`).join('')}
              </ul>
            </div>
            ` : ''}
            
            ${result.feedback?.recommendations && result.feedback.recommendations.length > 0 ? `
            <div class="recommendations">
              <h5>💡 Recommendations</h5>
              <ul>
                ${result.feedback.recommendations.map((rec: string) => `<li>${rec}</li>`).join('')}
              </ul>
            </div>
            ` : ''}
          </div>
        </div>
      `;
    }).join('');

  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Results Analysis Report</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            padding: 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }
        
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            text-align: center;
        }
        
        .header h1 {
            font-size: 2.5rem;
            margin-bottom: 10px;
            font-weight: 700;
        }
        
        .header p {
            font-size: 1.1rem;
            opacity: 0.9;
        }
        
        .content {
            padding: 30px;
        }
        
        .summary-section {
            background: #f8fafc;
            border-radius: 8px;
            padding: 25px;
            margin-bottom: 30px;
        }
        
        .summary-stats {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin: 20px 0;
        }
        
        .stat-card {
            background: white;
            padding: 20px;
            border-radius: 8px;
            text-align: center;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .stat-card.compliant { border-left: 4px solid #10b981; }
        .stat-card.non-compliant { border-left: 4px solid #ef4444; }
        .stat-card.partial { border-left: 4px solid #f59e0b; }
        .stat-card.percentage { border-left: 4px solid #8b5cf6; }
        
        .stat-number {
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 5px;
        }
        
        .stat-card.compliant .stat-number { color: #10b981; }
        .stat-card.non-compliant .stat-number { color: #ef4444; }
        .stat-card.partial .stat-number { color: #f59e0b; }
        .stat-card.percentage .stat-number { color: #8b5cf6; }
        
        .stat-label {
            color: #6b7280;
            font-size: 0.9rem;
        }
        
        .progress-bar {
            width: 100%;
            height: 12px;
            background-color: #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
            margin: 20px 0;
        }
        
        .progress-fill {
            height: 100%;
            transition: width 0.3s ease;
        }
        
        .progress-fill.low { background-color: #ef4444; }
        .progress-fill.medium { background-color: #f59e0b; }
        .progress-fill.high { background-color: #10b981; }
        
        .section-title {
            font-size: 1.5rem;
            font-weight: 600;
            margin: 30px 0 20px 0;
            color: #1f2937;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 10px;
        }
        
        .subsection-title {
            font-size: 1.1rem;
            font-weight: 600;
            margin: 20px 0 15px 0;
            color: #374151;
        }
        
        .results-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 30px 0 20px 0;
            flex-wrap: wrap;
            gap: 15px;
        }
        
        .control-buttons {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
        }
        
        .control-btn {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #374151;
            cursor: pointer;
            transition: all 0.2s;
        }
        
        .control-btn:hover {
            background: #f9fafb;
            border-color: #9ca3af;
        }
        
        .btn-icon {
            width: 16px;
            height: 16px;
        }
        
        .result-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .result-header {
            background: #f9fafb;
            padding: 15px 20px;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            transition: background-color 0.2s;
        }
        
        .result-header:hover {
            background: #f3f4f6;
        }
        
        .result-title {
            font-size: 1.1rem;
            font-weight: 600;
            color: #1f2937;
            text-transform: capitalize;
        }
        
        .header-actions {
            display: flex;
            align-items: center;
            gap: 12px;
        }
        
        .badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.8rem;
            font-weight: 600;
            text-transform: uppercase;
        }
        
        .expand-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background-color 0.2s;
        }
        
        .expand-btn:hover {
            background: #e5e7eb;
        }
        
        .expand-icon {
            width: 16px;
            height: 16px;
            transition: transform 0.2s;
        }
        
        .expand-icon.expanded {
            transform: rotate(90deg);
        }
        
        .badge.compliant {
            background-color: #dcfce7;
            color: #166534;
        }
        
        .badge.non-compliant {
            background-color: #fee2e2;
            color: #991b1b;
        }
        
        .badge.warning {
            background-color: #fef3c7;
            color: #92400e;
        }
        
        .result-content {
            padding: 20px;
        }
        
        .result-content h5 {
            font-size: 1rem;
            font-weight: 600;
            margin: 15px 0 8px 0;
            color: #374151;
        }
        
        .result-content p, .result-content li {
            color: #6b7280;
            margin-bottom: 8px;
        }
        
        .result-content ul {
            margin-left: 20px;
        }
        
        .analysis-details, .user-response {
            background: #f9fafb;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 15px;
        }
        
        .reasoning {
            background: #eff6ff;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 15px;
        }
        
        .evidence {
            background: #f0fdf4;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 15px;
        }
        
        .recommendations {
            background: #fffbeb;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 15px;
        }
        
        .details-text {
            color: #374151;
            line-height: 1.6;
        }
        
        .footer {
            background: #f9fafb;
            padding: 20px;
            text-align: center;
            color: #6b7280;
            border-top: 1px solid #e5e7eb;
        }
        
        @media print {
            body {
                background: white;
                padding: 0;
            }
            
            .container {
                box-shadow: none;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Results Analysis Report</h1>
            <p>Analysis completed on ${formattedDate}</p>
        </div>
        
        <div class="content">
            <div class="summary-section">
                <h2>Summary</h2>
                <div class="summary-stats">
                    <div class="stat-card compliant">
                        <div class="stat-number">${analysisData.summary.compliant}</div>
                        <div class="stat-label">Compliant</div>
                    </div>
                    <div class="stat-card non-compliant">
                        <div class="stat-number">${analysisData.summary.nonCompliant}</div>
                        <div class="stat-label">Non-Compliant</div>
                    </div>
                    <div class="stat-card percentage">
                        <div class="stat-number">${analysisData.summary.compliancePercentage}%</div>
                        <div class="stat-label">Compliance Rate</div>
                    </div>
                </div>
                
                <div class="progress-bar">
                    <div class="progress-fill ${analysisData.summary.compliancePercentage < 50 ? 'low' : analysisData.summary.compliancePercentage < 80 ? 'medium' : 'high'}" 
                         style="width: ${analysisData.summary.compliancePercentage}%"></div>
                </div>
            </div>
            
            <div class="results-controls">
                <h3 class="section-title">Detailed Results</h3>
                <div class="control-buttons">
                    <button class="control-btn" onclick="expandAllCards()">Expand All</button>
                    <button class="control-btn" onclick="collapseAllCards()">Collapse All</button>
                </div>
            </div>
            
            <div class="results-section">
                <h4 class="subsection-title">AI Analysis Results</h4>
                ${aiResultsHTML}
            </div>
            
            ${userQuestionsHTML ? `
            <div class="results-section">
                <h4 class="subsection-title">Auditor Declared Questions Results</h4>
                ${userQuestionsHTML}
            </div>
            ` : ''}
        </div>
        
        <div class="footer">
            <p>Generated on ${formattedDate} | Results Analysis Report</p>
        </div>
    </div>
    
    <script>
        // Expand/Collapse functionality
        function toggleCard(checkId) {
            const content = document.getElementById('content-' + checkId);
            const expandIcon = document.querySelector('[onclick*="' + checkId + '"] .expand-icon');
            
            if (content.style.display === 'none' || content.style.display === '') {
                content.style.display = 'block';
                if (expandIcon) {
                    expandIcon.classList.add('expanded');
                }
            } else {
                content.style.display = 'none';
                if (expandIcon) {
                    expandIcon.classList.remove('expanded');
                }
            }
        }
        
        function expandAllCards() {
            const allContents = document.querySelectorAll('[id^="content-"]');
            const allIcons = document.querySelectorAll('.expand-icon');
            
            allContents.forEach(content => {
                content.style.display = 'block';
            });
            
            allIcons.forEach(icon => {
                icon.classList.add('expanded');
            });
        }
        
        function collapseAllCards() {
            const allContents = document.querySelectorAll('[id^="content-"]');
            const allIcons = document.querySelectorAll('.expand-icon');
            
            allContents.forEach(content => {
                content.style.display = 'none';
            });
            
            allIcons.forEach(icon => {
                icon.classList.remove('expanded');
            });
        }
    </script>
</body>
</html>
  `;
};

const AnalysisDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [analysisData, setAnalysisData] = useState<StoredAnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  
  // Debug user answers when analysis data is loaded
  useEffect(() => {
    if (analysisData) {
      console.log('🔍 AnalysisDetail - Analysis data loaded:', analysisData);
      console.log('🔍 AnalysisDetail - User answers:', analysisData.userAnswers);
      console.log('🔍 AnalysisDetail - User answers count:', analysisData.userAnswers?.length || 0);
      console.log('🔍 AnalysisDetail - Results keys:', Object.keys(analysisData.results));
      console.log('🔍 AnalysisDetail - User question results:', Object.keys(analysisData.results).filter(key => key.startsWith('user_question_')));
    }
  }, [analysisData]);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState<string | null>(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState<string>("");

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!id || !currentUser) {
        setError("Invalid analysis ID or not authenticated");
        setLoading(false);
        return;
      }

      try {
        console.log(`Fetching analysis with ID: ${id}`);
        const data = await getAnalysisById(id, currentUser.uid);
        
        if (!data) {
          setError("Analysis not found");
          return;
        }

        console.log("Analysis data loaded:", data);
        console.log("Analysis createdAt:", data.createdAt, "type:", typeof data.createdAt);
        console.log("Analysis timestamp:", data.timestamp, "type:", typeof data.timestamp);
        if (data.createdAt && typeof data.createdAt === 'object') {
          console.log("createdAt object keys:", Object.keys(data.createdAt));
          console.log("createdAt object:", JSON.stringify(data.createdAt, null, 2));
        }
        setAnalysisData(data);
        
      } catch (error) {
        console.error("Error fetching analysis:", error);
        setError(error instanceof Error ? error.message : "Failed to load analysis");
        toast({
          title: "Error",
          description: "Failed to load analysis data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [id, currentUser, toast]);

  const handleBackClick = () => {
    navigate("/dashboard/history");
  };

  const handleExportPDF = () => {
    toast({
      title: "Feature Coming Soon",
      description: "PDF export will be available in a future update.",
    });
  };

  const downloadResultsAsHTML = () => {
    if (!analysisData) return;

    const htmlContent = generateHTMLReport(analysisData);
    
    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analysis-report-${analysisData.companyName?.replace(/[^a-zA-Z0-9]/g, '-')}-${formatTimestamp(analysisData.createdAt || analysisData.timestamp, 'yyyy-MM-dd')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Successful",
      description: "Analysis report has been downloaded as HTML.",
    });
  };

  const handleViewDocument = (documentInfo: any, documentName: string) => {
    // Since we're now storing metadata instead of files, we can't view documents directly
    toast({
      title: "Document Viewing",
      description: "Document viewing is not available for this analysis format.",
      variant: "default",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={handleBackClick} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to History
        </Button>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Analysis not found</p>
        <Button onClick={handleBackClick} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to History
        </Button>
      </div>
    );
  }

  // Document type labels for display
  const documentLabels: Record<string, string> = {
    audit_report: "Audit Report",
    annexure_a: "Annexure A",
    annexure_b: "Annexure B", 
    balance_sheet: "Balance Sheet",
    notes: "Notes to Financial Statements",
    pl_notes: "P&L Notes",
    annual_report: "Annual Report",
    secretarial_compliance: "Secretarial Compliance",
    cfs: "Consolidated Financial Statements"
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                onClick={handleBackClick} 
                variant="outline" 
                size="sm"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold">
                  {analysisData.companyName?.replace(/QQ(\d)/g, 'Q$1')}
                </h1>
                <p className="text-gray-500">
                  Analysis performed on {formatTimestamp(analysisData.createdAt || analysisData.timestamp)}
                </p>
              </div>
            </div>
        <div className="flex space-x-2">
          <Button onClick={downloadResultsAsHTML} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export HTML
          </Button>
          <Button onClick={handleExportPDF} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {analysisData.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {analysisData.summary.total}
            </div>
            <div className="text-sm text-blue-800">Total Checks</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {analysisData.summary.compliant}
            </div>
            <div className="text-sm text-green-800">Compliant</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {analysisData.summary.nonCompliant}
            </div>
            <div className="text-sm text-red-800">Non-Compliant</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {analysisData.summary.compliancePercentage}%
            </div>
            <div className="text-sm text-purple-800">Compliance Rate</div>
          </div>
        </div>
      )}

      {/* Documents Section */}
      {analysisData.documents && Object.keys(analysisData.documents).length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Analyzed Documents
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(analysisData.documents).map(([docType, docInfo]) => {
              if (!docInfo) return null;
              
              const label = documentLabels[docType] || docType;
              
              return (
                <div
                  key={docType}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-sm truncate">{label}</h3>
                      <p className="text-xs text-gray-500 truncate">
                        {docInfo.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {(docInfo.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDocument(docInfo, label)}
                      disabled
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}      {/* Analysis Results */}
      {analysisData.results && (
        <div className="bg-white rounded-lg shadow-sm">
          <AnalysisResults 
            results={analysisData.results}
            parameters={analysisData.parameters}
          />
        </div>
      )}      {/* Document Viewer Modal */}
      {viewerOpen && selectedDocumentUrl && (
        <SimpleDocumentViewer
          isOpen={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            if (selectedDocumentUrl) {
              URL.revokeObjectURL(selectedDocumentUrl);
            }
            setSelectedDocumentUrl(null);
            setSelectedDocumentName("");
          }}
          url={selectedDocumentUrl}
          title={selectedDocumentName}
        />
      )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisDetail;
